import type {GlobalAgentsMenuBaseProps} from '@github-ui/global-agents-menu/GlobalAgentsMenu'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import type {GlobalCreateMenuProps} from '@github-ui/global-create-menu'
import {useQuery} from '@github-ui/react-query'
import {createPersister} from '@github-ui/react-query-persister'
import {useAnalytics} from '@github-ui/use-analytics'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {createContext, type PropsWithChildren, use, useMemo} from 'react'

import type {GlobalNavMenuProps} from '../menu/GlobalNavMenu'
import {useTopNavConfig} from '../primitives/TopNav'
import type {AccountSwitchDialogProps} from '../user-menu/AccountSwitchDialog'
import type {GlobalNavUserMenuProps} from '../user-menu/types'
import {getCurrentOwnerRepo} from './current-owner-repo'
import {DeferredNavPayloadValidator} from './payload-validators'

const ONE_DAY_MS = 24 * 60 * 60 * 1000

export interface DeferredNavPayload {
  nav_menu: GlobalNavMenuProps
  user_menu: GlobalNavUserMenuProps
  create_menu: GlobalCreateMenuProps
  agents_menu: Omit<GlobalAgentsMenuBaseProps, 'copilotApiUrl'>
  account_switch_dialog: AccountSwitchDialogProps
}

type DeferredPayloadSliceKey = keyof DeferredNavPayload

interface DeferredNavPayloadContextValue {
  data: DeferredNavPayload | undefined
  isError: boolean
  isPending: boolean
}

const DeferredNavPayloadContext = createContext<DeferredNavPayloadContextValue>({
  data: undefined,
  isError: false,
  isPending: false,
})

async function fetchPayload<T>(url: string): Promise<T> {
  const response = await verifiedFetchJSON(url)
  if (response.ok) {
    try {
      return (await response.json()) as T
    } catch {
      throw new Error('Failed to parse nav payload.')
    }
  }
  throw new Error('Failed to retrieve nav payload.')
}

const SLICE_QUERY_KEYS: Record<DeferredPayloadSliceKey, string> = {
  nav_menu: 'global-nav-menu',
  user_menu: 'global-user-menu',
  create_menu: 'global-create-menu',
  agents_menu: 'global-agents-menu',
  account_switch_dialog: 'account-switch-dialog',
}

export function DeferredNavPayloadProvider({children}: PropsWithChildren) {
  const {payloadsBaseUrl} = useTopNavConfig()
  const {sendAnalyticsEvent} = useAnalytics()
  const queryKey = ['global-nav-deferred', payloadsBaseUrl]

  const persister = useMemo(
    () =>
      createPersister({
        validator: DeferredNavPayloadValidator,
        sendAnalyticsEvent,
        prefix: 'global-nav-deferred',
        maxAgeMs: ONE_DAY_MS,
        alwaysRefetch: false,
      }),
    [sendAnalyticsEvent],
  )

  const {data, isError, isPending} = useQuery({
    queryKey,
    queryFn: () => fetchPayload<DeferredNavPayload>(payloadsBaseUrl),
    persister,
    staleTime: ONE_DAY_MS,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  })

  const value = useMemo(() => ({data, isError, isPending}), [data, isError, isPending])

  return <DeferredNavPayloadContext value={value}>{children}</DeferredNavPayloadContext>
}

/**
 * Overlay repo/org-contextual fields onto a cached payload slice. The cached
 * payload was fetched without context query params, so any fields that depend
 * on `current_repository` / `current_organization` need to be re-derived on
 * the client from the `<meta name="current-repo-nwo">` tag.
 */
function applyContextOverlay<K extends DeferredPayloadSliceKey>(
  key: K,
  data: DeferredNavPayload[K] | undefined,
): DeferredNavPayload[K] | undefined {
  if (!data) return data

  if (key === 'user_menu') {
    const userMenu = data as DeferredNavPayload['user_menu']
    if (typeof window !== 'undefined' && userMenu.addAccountPath) {
      const url = new URL(userMenu.addAccountPath, window.location.origin)
      if (!url.searchParams.has('return_to')) {
        url.searchParams.set('return_to', window.location.href)
        return {...userMenu, addAccountPath: url.pathname + url.search} as DeferredNavPayload[K]
      }
    }
    return data
  }

  const ownerRepo = getCurrentOwnerRepo()
  if (!ownerRepo) return data

  if (key === 'agents_menu') {
    const agents = data as DeferredNavPayload['agents_menu']
    // Only overlay if the cached payload doesn't already include a repository
    // (i.e. it was fetched without context params).
    if (agents.repository) return data
    return {
      ...agents,
      // Consumers (`GlobalCopilotOverlay`) only read `name` and `ownerLogin`.
      // Other fields on the Repository type are unused here; cast to satisfy
      // the type without fabricating values we can't know client-side.
      repository: {name: ownerRepo.repo, ownerLogin: ownerRepo.owner} as NonNullable<
        DeferredNavPayload['agents_menu']['repository']
      >,
    } as DeferredNavPayload[K]
  }

  if (key === 'create_menu') {
    const createMenu = data as DeferredNavPayload['create_menu']
    if (createMenu.owner && createMenu.repo) return data
    return {
      ...createMenu,
      owner: ownerRepo.owner,
      repo: ownerRepo.repo,
    } as DeferredNavPayload[K]
  }

  return data
}

/**
 * Returns the resolved data for the given payload slice key, along with a
 * pending indicator.
 *
 * Data comes from the unified deferred response. If that request fails,
 * an individual fetch for this slice is made automatically as a fallback.
 */
export function useDeferredPayloadSlice<K extends DeferredPayloadSliceKey>(
  key: K,
): {data: DeferredNavPayload[K] | undefined; isPending: boolean} {
  const {data: combinedData, isError, isPending: combinedIsPending} = use(DeferredNavPayloadContext)

  const {getPayloadsUrl} = useTopNavConfig()
  const payloadsUrl = getPayloadsUrl(key)

  const sliceQueryKey = [SLICE_QUERY_KEYS[key], payloadsUrl]

  const {data: individualData, isPending: individualIsPending} = useQuery({
    queryKey: sliceQueryKey,
    queryFn: () => fetchPayload<DeferredNavPayload[K]>(payloadsUrl),
    enabled: isError, // Only fetch individually if the combined fetch failed
  })

  const rawData = combinedData?.[key] ?? individualData
  const data = applyContextOverlay(key, rawData)
  const isPending = data === undefined && ((!isError && combinedIsPending) || (isError && individualIsPending))

  return {data, isPending}
}
